import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useSensors } from '../../hooks/useSensors';
import { useSensorNames } from '../../context/SensorNamesContext';
import { useSession } from '../../context/SessionContext';
import { updateDeviceLocation } from '../../services/sensors';
import { DEMO_WEATHER_STATIONS } from '../../data/demo';
import { markerLabel } from '../../utils/deviceMarker';
import { agregarCapaOrtofoto } from '../../utils/capaOrtofoto';
import { useOrtofoto } from '../../hooks/useOrtofoto';

// ── Unified device type ───────────────────────────────────────

interface LocDevice {
  id: string;
  imei: string;
  name: string;
  lat: number;
  lng: number;
  status: 'good' | 'warn' | 'alert' | 'offline';
  deviceType: 'soil' | 'weather' | 'pest';
}

type Positions = Record<string, { lat: number; lng: number }>;

/**
 * Posiciones que quedaron guardadas SOLO en este navegador.
 *
 * Hasta ahora arrastrar un marcador escribía acá y en ningún otro lado,
 * aunque el panel dijera "Changes save automatically". El resultado era que
 * esta pantalla mostraba la ubicación corregida y el resto de la plataforma
 * —dashboard, suelo, administración— seguía mostrando la vieja, sin que nada
 * avisara de la discrepancia.
 *
 * Se sigue leyendo la llave para no perder ese trabajo: si hay posiciones
 * locales que no coinciden con el servidor, se ofrece subirlas. Una vez
 * subidas (o descartadas), la llave se borra y ya no se vuelve a escribir.
 */
const POS_KEY = 'sensor_positions';

function loadPositions(): Positions {
  try { return JSON.parse(localStorage.getItem(POS_KEY) ?? '{}'); }
  catch { return {}; }
}

// ── Page ──────────────────────────────────────────────────────

export default function LocationPage() {
  const { t } = useTranslation();
  const { sensors, loading, refresh } = useSensors();
  const { getName } = useSensorNames();
  const { isDemoTenant } = useSession();
  /** Posición recién arrastrada, mientras el servidor confirma y llega el refetch. */
  const [optimista, setOptimista] = useState<Positions>({});
  const [guardando, setGuardando] = useState<string | null>(null);
  const [guardado, setGuardado] = useState<string | null>(null);
  const [fallo, setFallo] = useState<string | null>(null);
  const [heredadas, setHeredadas] = useState<Positions>(loadPositions);
  const [subiendo, setSubiendo] = useState(false);
  const [soilOpen, setSoilOpen] = useState(true);
  const [weatherOpen, setWeatherOpen] = useState(true);

  // Build unified device list
  const soilDevices: LocDevice[] = sensors.map(s => ({
    id: s.id, imei: s.imei, name: s.name,
    lat: s.lat, lng: s.lng,
    status: s.status as LocDevice['status'],
    deviceType: (s.type ?? 'soil') as LocDevice['deviceType'],
  }));

  /**
   * Estaciones meteorológicas de demostración: SOLO para el tenant Demo.
   *
   * Se agregaban siempre, así que un cliente real veía tres estaciones
   * inventadas —en Georgia, Estados Unidos— presentadas como suyas, y el mapa
   * se centraba en el promedio de sus coordenadas con las del sensor real:
   * un punto en medio de la nada. Mismo criterio que useSensors: los datos
   * demo son una decisión del servidor, no un relleno para cuando falta algo.
   */
  const weatherDevices: LocDevice[] = isDemoTenant
    ? DEMO_WEATHER_STATIONS.map(s => ({
        id: s.id, imei: s.imei, name: s.name,
        lat: s.lat, lng: s.lng,
        status: s.status,
        deviceType: 'weather' as const,
      }))
    : [];

  const allDevices = [...soilDevices, ...weatherDevices];

  const getPos = (d: LocDevice) => optimista[d.imei] ?? { lat: d.lat, lng: d.lng };

  /**
   * Posiciones locales que NO coinciden con lo que tiene el servidor. Son las
   * que se arrastraron antes de que esto persistiera de verdad, así que hay
   * que ofrecer subirlas en vez de tirarlas: para quien las movió, ésa es la
   * ubicación correcta y la de la base es la vieja.
   */
  const porSubir = soilDevices.filter(d => {
    const local = heredadas[d.imei];
    if (!local) return false;
    return Math.abs(local.lat - d.lat) > 1e-6 || Math.abs(local.lng - d.lng) > 1e-6;
  });

  const olvidarHeredadas = () => {
    localStorage.removeItem(POS_KEY);
    setHeredadas({});
  };

  const subirHeredadas = async () => {
    setSubiendo(true);
    try {
      for (const d of porSubir) {
        const local = heredadas[d.imei]!;
        await updateDeviceLocation(d.id, local.lat, local.lng);
      }
      olvidarHeredadas();
      await refresh();
    } catch {
      setFallo('__migracion__');
    } finally {
      setSubiendo(false);
    }
  };

  const handleDragEnd = async (imei: string, lat: number, lng: number) => {
    const device = soilDevices.find(d => d.imei === imei);
    if (!device) return;   // las demo no se guardan: no existen en la base

    setOptimista(p => ({ ...p, [imei]: { lat, lng } }));
    setGuardando(imei);
    setFallo(null);
    try {
      await updateDeviceLocation(device.id, lat, lng);
      setGuardado(imei);
      setTimeout(() => setGuardado(p => (p === imei ? null : p)), 2500);
      await refresh();
      // El refetch ya trae la coordenada nueva; se suelta la optimista para
      // que la pantalla vuelva a reflejar exactamente lo que hay en la base.
      setOptimista(p => { const n = { ...p }; delete n[imei]; return n; });
    } catch {
      // Se revierte: dejar el marcador donde lo soltó el usuario después de
      // un error lo haría creer que quedó guardado, que es justo el problema
      // que esta pantalla tenía.
      setOptimista(p => { const n = { ...p }; delete n[imei]; return n; });
      setFallo(imei);
    } finally {
      setGuardando(p => (p === imei ? null : p));
    }
  };

  if (loading) {
    return (
      <div className="page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ fontSize: 13, color: 'var(--gray)' }}>Loading devices…</span>
      </div>
    );
  }

  const allLats = allDevices.map(d => d.lat);
  const allLngs = allDevices.map(d => d.lng);
  const center: [number, number] = [
    allLats.reduce((a, b) => a + b, 0) / (allLats.length || 1),
    allLngs.reduce((a, b) => a + b, 0) / (allLngs.length || 1),
  ];

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>

      {/* ── Left panel ─────────────────────────────────────────── */}
      <div style={{
        width: 272, flexShrink: 0,
        borderRight: '1px solid var(--border)',
        background: 'var(--white)',
        display: 'flex', flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div style={{ padding: '20px 18px 14px', borderBottom: '1px solid var(--border)' }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--dark)' }}>{t('locations.title')}</div>
          <div style={{ fontSize: 12, color: 'var(--gray)', marginTop: 4, lineHeight: 1.5 }}>
            Drag any marker on the map to reposition it. Changes are saved on the server.
          </div>
        </div>

        {/* Posiciones que quedaron sólo en este navegador — ver POS_KEY. */}
        {porSubir.length > 0 && (
          <div style={{
            margin: '10px 10px 0', padding: '11px 12px', borderRadius: 10,
            border: '1.5px solid var(--amber)', background: '#fffbeb',
          }}>
            <div style={{ fontSize: 11.5, color: 'var(--dark)', lineHeight: 1.5, marginBottom: 8 }}>
              {porSubir.length === 1 ? 'One position was' : `${porSubir.length} positions were`} saved
              only in this browser and never reached the server — that is why the rest of the
              platform still shows the old location.
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                onClick={subirHeredadas}
                disabled={subiendo}
                style={{
                  fontSize: 11, fontWeight: 600, color: 'var(--white)',
                  background: 'var(--green)', border: 'none', borderRadius: 6,
                  padding: '5px 10px', cursor: subiendo ? 'default' : 'pointer', opacity: subiendo ? 0.6 : 1,
                }}
              >
                {subiendo ? 'Saving…' : 'Save to server'}
              </button>
              <button
                onClick={olvidarHeredadas}
                disabled={subiendo}
                style={{
                  fontSize: 11, color: 'var(--gray)', background: 'var(--white)',
                  border: '1px solid var(--border)', borderRadius: 6,
                  padding: '5px 10px', cursor: subiendo ? 'default' : 'pointer',
                }}
              >
                {t('locations.discard')}
              </button>
            </div>
            {fallo === '__migracion__' && (
              <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 7 }}>
                {t('locations.saveFailed')}
              </div>
            )}
          </div>
        )}

        <div style={{ flex: 1, overflowY: 'auto', padding: '10px 10px' }}>
          {/* Soil Sensors */}
          <SectionHeader label={t('locations.soilSensors')} count={soilDevices.length} open={soilOpen} onToggle={() => setSoilOpen(o => !o)} />
          {soilOpen && soilDevices.map(d => (
            <DeviceCard
              key={d.id}
              device={d}
              displayName={getName(d.imei, d.name)}
              pos={getPos(d)}
              estado={
                guardando === d.imei ? 'guardando' :
                guardado === d.imei ? 'guardado' :
                fallo === d.imei ? 'fallo' : 'quieto'
              }
            />
          ))}

          {/* Weather Stations — sólo si hay alguna (ver weatherDevices). */}
          {weatherDevices.length > 0 && (
            <SectionHeader label={t('locations.weatherStations')} count={weatherDevices.length} open={weatherOpen} onToggle={() => setWeatherOpen(o => !o)} />
          )}
          {weatherOpen && weatherDevices.map(d => (
            <DeviceCard
              key={d.id}
              device={d}
              displayName={getName(d.imei, d.name)}
              pos={getPos(d)}
              estado="quieto"
            />
          ))}
        </div>

        <div style={{ padding: '10px 16px', borderTop: '1px solid var(--border)' }}>
          <div style={{ fontSize: 11, color: 'var(--gray)', lineHeight: 1.5 }}>
            💡 Drag markers to reposition. Scroll to zoom.
          </div>
        </div>
      </div>

      {/* ── Map ────────────────────────────────────────────────── */}
      <div style={{ flex: 1, position: 'relative' }}>
        <DraggableMap
          center={center}
          zoom={14}
          devices={allDevices}
          positions={optimista}
          onDragEnd={handleDragEnd}
          getName={getName}
        />
      </div>
    </div>
  );
}

// ── Left panel sub-components ─────────────────────────────────

function SectionHeader({ label, count, open, onToggle }: {
  label: string; count: number; open: boolean; onToggle: () => void;
}) {
  return (
    <div
      onClick={onToggle}
      style={{
        fontSize: 10, fontWeight: 700, color: 'var(--gray)',
        textTransform: 'uppercase', letterSpacing: '0.08em',
        padding: '8px 4px 6px', display: 'flex', alignItems: 'center', gap: 6,
        cursor: 'pointer', userSelect: 'none',
      }}
    >
      <span style={{
        fontSize: 9, display: 'inline-block',
        transform: open ? 'rotate(0deg)' : 'rotate(-90deg)',
        transition: 'transform 0.15s',
      }}>▾</span>
      {label}
      <span style={{ fontWeight: 400 }}>({count})</span>
    </div>
  );
}

type EstadoTarjeta = 'quieto' | 'guardando' | 'guardado' | 'fallo';

function DeviceCard({ device, displayName, pos, estado }: {
  device: LocDevice;
  displayName: string;
  pos: { lat: number; lng: number };
  estado: EstadoTarjeta;
}) {
  const { t } = useTranslation();
  const statusColor =
    device.status === 'good'    ? 'var(--green)' :
    device.status === 'warn'    ? 'var(--amber)' :
    device.status === 'offline' ? 'var(--gray)'  : 'var(--red)';

  return (
    <div style={{
      padding: '11px 13px', borderRadius: 10, marginBottom: 6,
      border: `1.5px solid ${estado === 'guardado' ? 'var(--green)' : estado === 'fallo' ? 'var(--red)' : 'var(--border)'}`,
      background: estado === 'guardado' ? 'var(--green-bg)' : 'var(--bg)',
      transition: 'border-color 0.3s, background 0.3s',
    }}>
      {/* Name row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 7 }}>
        <div style={{ width: 7, height: 7, borderRadius: '50%', background: statusColor, flexShrink: 0 }} />
        <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--dark)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayName}
        </div>
        <span style={{
          fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 4,
          background: device.deviceType === 'weather' ? '#dbeafe' : 'var(--green-bg)',
          color: device.deviceType === 'weather' ? '#1d4ed8' : 'var(--green-dk)',
        }}>
          {markerLabel(device.deviceType)}
        </span>
        {estado === 'guardando' && <span style={{ fontSize: 10, color: 'var(--gray)' }}>Saving…</span>}
        {estado === 'guardado' && <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 700 }}>✓ Saved</span>}
        {estado === 'fallo' && <span style={{ fontSize: 10, color: 'var(--red)', fontWeight: 600 }}>{t('locations.notSaved')}</span>}
      </div>

      {/* Coordinates */}
      <div style={{ fontSize: 10, color: 'var(--gray)', fontFamily: 'monospace', lineHeight: 1.9 }}>
        <div>Lat:&nbsp; {pos.lat.toFixed(6)}</div>
        <div>Lng: {pos.lng.toFixed(6)}</div>
      </div>

      {estado === 'fallo' && (
        <div style={{ fontSize: 10.5, color: 'var(--red)', marginTop: 7, lineHeight: 1.45 }}>
          The marker went back to its saved position. Try dragging it again.
        </div>
      )}
    </div>
  );
}

// ── Draggable map ─────────────────────────────────────────────

interface MapProps {
  center: [number, number];
  zoom: number;
  devices: LocDevice[];
  positions: Positions;
  onDragEnd: (imei: string, lat: number, lng: number) => void;
  getName: (imei: string, fallback: string) => string;
}

function DraggableMap({ center, zoom, devices, positions, onDragEnd, getName }: MapProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const onDragEndRef = useRef(onDragEnd);
  useEffect(() => { onDragEndRef.current = onDragEnd; });

  const { t } = useTranslation();
  const { vuelo } = useOrtofoto();

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    // maxZoom 21: ubicar un sensor sobre la imagen del cliente, con
    // centímetros por pixel, es mucho más preciso que hacerlo sobre una
    // satelital borrosa — pero sólo si el mapa deja acercarse hasta allá.
    // Se arranca en una vista cualquiera: el encuadre real lo pone el
    // efecto de abajo, en cuanto se sepa dónde están los sensores.
    const map = L.map(containerRef.current, { maxZoom: 21 }).setView(center, zoom);
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
      { attribution: 'ESRI', maxZoom: 21, maxNativeZoom: 19 },
    ).addTo(map);
    L.tileLayer(
      'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
      { opacity: 0.7, maxZoom: 21, maxNativeZoom: 19 },
    ).addTo(map);
    mapRef.current = map;
    return () => { map.remove(); mapRef.current = null; };
  }, []);

  /** Imagen del cliente como fondo — el mismo control que en los demás mapas. */
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !vuelo) return;
    return agregarCapaOrtofoto(map, vuelo, t('maps.imageLayer'));
  }, [vuelo, t]);


  /**
   * Encuadra para que se vean todos los sensores, UNA sola vez.
   *
   * Antes la vista se ponía centrando en el PROMEDIO de las coordenadas a
   * zoom fijo 14. Con los sensores juntos se ve bien; en cuanto uno se
   * aleja, ese promedio cae en medio de la nada y el mapa abre acercado
   * sobre un punto donde no hay nada que ver. Con tres sensores en
   * Monterrey y uno en Georgia el promedio da 27.6, −96.2: mar abierto en
   * el Golfo, donde ni siquiera hay imagen satelital y sale el recuadro de
   * "Map data not yet available".
   *
   * Es el mismo error que ya se había corregido en el mapa de sensores;
   * esta pantalla se quedó con la versión vieja.
   *
   * Y va una sola vez A PROPÓSITO. Ésta es la pantalla donde se arrastran
   * los marcadores: reencuadrar cada vez que una posición cambia movería
   * el mapa justo mientras alguien lo está usando, que es exactamente de
   * lo que se quejaba antes. Después del primer encuadre, la vista es del
   * usuario y no se toca.
   */
  const yaEncuadro = useRef(false);
  useEffect(() => {
    const map = mapRef.current;
    if (!map || yaEncuadro.current) return;
    const puntos = devices
      .map(d => positions[d.imei] ?? { lat: d.lat, lng: d.lng })
      .filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lng) && (p.lat !== 0 || p.lng !== 0))
      .map(p => [p.lat, p.lng] as [number, number]);
    if (puntos.length === 0) return;
    yaEncuadro.current = true;
    // maxZoom 17 para que un sensor solo no abra pegado al suelo, donde no
    // se distingue nada de alrededor.
    map.fitBounds(L.latLngBounds(puntos), { padding: [40, 40], maxZoom: 17 });
  }, [devices, positions]);

  useEffect(() => {
    if (!mapRef.current) return;
    const map = mapRef.current;
    map.eachLayer(layer => { if (layer instanceof L.Marker) map.removeLayer(layer); });

    devices.forEach(d => {
      const pos = positions[d.imei] ?? { lat: d.lat, lng: d.lng };
      if (!pos.lat || !pos.lng || isNaN(pos.lat) || isNaN(pos.lng)) return;

      const statusForColor = d.status === 'offline' ? 'warn' : d.status;
      const color =
        statusForColor === 'good'  ? '#1D9E75' :
        statusForColor === 'warn'  ? '#EF9F27' : '#E24B4A';

      // Weather stations get a slightly different shape: rounded square
      const isWeather = d.deviceType === 'weather';
      const label = markerLabel(d.deviceType);

      const icon = L.divIcon({
        html: `
          <div style="display:flex;flex-direction:column;align-items:center;">
            <div style="
              width:38px;height:38px;
              border-radius:${isWeather ? '8px' : '50%'};
              background:${color};color:#fff;
              display:flex;align-items:center;justify-content:center;
              font-size:10px;font-weight:700;letter-spacing:0.03em;
              box-shadow:0 3px 10px rgba(0,0,0,0.45);
              border:2.5px solid #fff;
              cursor:grab;user-select:none;
            ">${label}</div>
            <div style="
              width:0;height:0;
              border-left:6px solid transparent;
              border-right:6px solid transparent;
              border-top:9px solid ${color};
              margin-top:-1px;
            "></div>
          </div>`,
        className: '',
        iconSize: [38, 47],
        iconAnchor: [19, 47],
        tooltipAnchor: [0, -50],
      });

      const marker = L.marker([pos.lat, pos.lng], { icon, draggable: true }).addTo(map);

      marker.bindTooltip(
        `<strong>${getName(d.imei, d.name)}</strong><br><span style="color:#888;font-size:11px">${isWeather ? 'Weather Station' : 'Soil Sensor'}</span><br>Drag to reposition`,
        { direction: 'top', offset: [0, -50], opacity: 0.92 },
      );

      marker.on('dragend', (e: L.LeafletEvent) => {
        const ll = (e.target as L.Marker).getLatLng();
        onDragEndRef.current(d.imei, ll.lat, ll.lng);
      });
    });
  }, [devices, positions]);

  return <div ref={containerRef} style={{ width: '100%', height: '100%' }} />;
}
